import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Calendar,
  Clock,
  CheckCircle,
  FileText,
  Briefcase
} from "lucide-react";
import { Link } from "wouter";
import { CandidateLayout } from "@/components/layouts/CandidateLayout";
import { CandidateApplicationSwimLanes } from "@/components/CandidateApplicationSwimLanes";
import { useCandidateSSE } from "@/hooks/useSSE";
import type { Application, MockInterviewSession } from "@shared/schema";

// Extended application type with nested relationships
type ApplicationWithDetails = Application & {
  job?: {
    id: string;
    title: string;
    location?: string | null;
    employmentType?: string | null;
    slug?: string | null;
    companyProfile?: {
      companyName: string;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
  company?: {
    name?: string;
    companyName?: string;
    [key: string]: unknown;
  };
  status?: string;
  // Interview scheduling info (from InterviewRequest.confirmedSlot)
  interviewScheduled?: {
    start: string;
    end: string;
    meetingLink?: string;
  } | null;
};

export default function MyApplications() {
  const { isAuthenticated, isLoading, user } = useAuth();
  
  // Enable real-time SSE updates for candidates
  const { isConnected: sseConnected } = useCandidateSSE();
  
  // Debug SSE connection
  console.log('[MY-APPLICATIONS] 🔌 SSE Connected:', sseConnected);

  const { data: applications, isLoading: applicationsLoading, refetch } = useQuery<ApplicationWithDetails[]>({
    queryKey: ["/api/applications/candidate"],
    queryFn: async () => {
      console.log('[MY APPLICATIONS] Fetching candidate applications...');
      const response = await fetch("/api/applications/candidate");
      if (!response.ok) {
        throw new Error('Failed to fetch applications');
      }
      const data = await response.json();
      console.log('[MY APPLICATIONS] Received applications:', data.map((a: ApplicationWithDetails) => ({ id: a.id, status: a.statusV2 })));
      return data;
    },
    enabled: !!user && user.role === 'candidate',
    staleTime: 0, // Always consider stale for real-time updates
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: true, // Refetch when switching tabs
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  // Fetch practice interview sessions for statistics
  const { data: mockInterviewSessions = [] } = useQuery<MockInterviewSession[]>({
    queryKey: ["/api/mock-interview"],
    queryFn: async () => {
      const response = await fetch("/api/mock-interview");
      if (!response.ok) {
        throw new Error('Failed to fetch practice interview sessions');
      }
      return response.json();
    },
    enabled: !!user && user.role === 'candidate' && !!applications?.length,
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  if (isLoading || applicationsLoading) {
    return (
      <div className="min-h-screen bg-base-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-base-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated || !user || user.role !== 'candidate') {
    return null;
  }

  // Calculate summary statistics
  const totalApplications = applications?.length || 0;
  const interviewsRequested = applications?.filter((app) => 
    ['interview_requested', 'interview_planned'].includes(app.statusV2?.toLowerCase() || '')
  ).length || 0;
  const mockInterviewsCompleted = mockInterviewSessions?.length || 0;
  const pendingApplications = applications?.filter((app) => 
    ['applied', 'viewed'].includes(app.statusV2?.toLowerCase() || '')
  ).length || 0;

  return (
    <CandidateLayout activeRoute="/my-applications">
      <div>
        <div className="p-8">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-base-text">My Applications</h1>
              <p className="text-base-text-muted mt-2">
                Track the status of your job applications and interviews
              </p>
            </div>

            {/* Clean Application Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <Card className="border-base-gray-200">
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-base-primary rounded-lg flex items-center justify-center">
                      <Briefcase className="w-6 h-6 text-white" />
                    </div>
                    <div className="ml-4">
                      <div className="text-2xl font-bold text-base-text">{totalApplications}</div>
                      <div className="text-sm text-base-text-muted">Total Applications</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-base-gray-200">
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-white" />
                    </div>
                    <div className="ml-4">
                      <div className="text-2xl font-bold text-base-text">{interviewsRequested}</div>
                      <div className="text-sm text-base-text-muted">Interview Opportunities</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-base-gray-200">
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <div className="ml-4">
                      <div className="text-2xl font-bold text-base-text">{mockInterviewsCompleted}</div>
                      <div className="text-sm text-base-text-muted">Practice Interviews</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-base-gray-200">
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div className="ml-4">
                      <div className="text-2xl font-bold text-base-text">{pendingApplications}</div>
                      <div className="text-sm text-base-text-muted">Under Review</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Applications Pipeline View */}
            {applications?.length === 0 ? (
              <Card className="border-base-gray-200">
                <CardContent className="p-12 text-center">
                  <FileText className="w-16 h-16 text-base-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-base-text mb-2">
                    No Applications Yet
                  </h3>
                  <p className="text-base-text-muted mb-6">
                    You haven't applied to any jobs yet. Start exploring opportunities!
                  </p>
                  <Link to="/job-search">
                    <Button className="bg-base-primary hover:bg-base-primary-hover text-white">
                      Browse Jobs
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <CandidateApplicationSwimLanes />
            )}
          </div>
        </div>
      </div>
    </CandidateLayout>
  );
}